import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { IWinnerItem, useStoreContext } from 'store/app';
import { useTranslationsContext } from 'store/translations';
import { Modal, ModalProps } from '../modal';

import Ticket from 'assets/img/ticket.png';
import WinBanner from 'assets/img/end-banner-victory.png';
import LoseBanner from 'assets/img/end-banner-unluck.png';
import './resultsModal.styl';
import { ITicket } from 'src/types';
import classNames from 'classnames';
import { sanitize } from 'dompurify';
import { IPrizeItemConfig, prizes } from './config';
import { WinnersModal } from './winnersModal';
import { PrizeModal } from '../prizeModal';
import Label from 'components/core/Label';

interface ResultsModalProps extends ModalProps {}

export const ResultsModal = observer((props: ResultsModalProps) => {
    const [winnersModalOpened, setWinnersModalOpened] = useState(false);
    const [prizeModalOpened, setPrizeModalOpened] = useState(false);
    const { translate } = useTranslationsContext();
    const { userState, logOperation, settings, isMob, openLink } = useStoreContext();

    const { wonTicket, wonPrizeKey } = useMemo((): {
        wonTicket: ITicket | undefined;
        wonPrizeKey: string | undefined;
    } => {
        let wonTicketLocal = undefined;
        let wonPrizeLocal = undefined;
        if (settings.winnersConf) {
            const winners = settings.winnersConf;
            wonTicketLocal = userState?.tickets?.find((ticket) => {
                const prize = Object.keys(winners).find((prizeKey) => {
                    return winners[prizeKey].ticketIds.includes(ticket.ticket_id);
                });

                if (prize) {
                    wonPrizeLocal = prize;
                    return true;
                }

                return false;
            });
        }

        return { wonTicket: wonTicketLocal, wonPrizeKey: wonPrizeLocal };
    }, [settings]);

    const wonPrize: IWinnerItem | undefined = useMemo(() => {
        return wonPrizeKey ? settings.winnersConf?.[wonPrizeKey] : undefined;
    }, [wonPrizeKey, settings]);

    const prizeConf: IPrizeItemConfig | undefined = useMemo(() => {
        let localConf = undefined;
        if (wonPrizeKey === 'superprize') {
            return {
                count: 8,
                id: 'superprize',
                imageUrl: 'string',
                itemNameKey: '100&nbsp;000&nbsp;₽'
            };
        }

        Object.values(prizes).find((group) => {
            const targetItem = group.items.find((item) => item.id === wonPrizeKey);
            if (targetItem) {
                localConf = targetItem;
                return;
            }
            return !!targetItem;
        });

        return localConf;
    }, [wonPrizeKey]);

    useEffect(() => {
        logOperation('raffle_results', 'render');
    }, []);

    const onClickPrimaryBtn = useCallback(() => {
        if (wonPrize?.modalConfig && settings.winnerTicketConf) {
            setPrizeModalOpened(true);
        } else {
            openLink('https://ok.ru/messages/561104947330', true);
        }
    }, [wonPrize]);

    return (
        <>
            <Modal
                title={translate('results.title')}
                className="results"
                secondaryButtonText={translate('results.button.tickets')}
                onClickSecondaryBtn={() => setWinnersModalOpened(true)}
                primaryButtonText={
                    wonPrize
                        ? translate(wonPrize.modalConfig ? 'results.button.link' : 'results.button.admin')
                        : undefined
                }
                onClickPrimaryBtn={onClickPrimaryBtn}
                {...props}
            >
                <div className="results-modal">
                    <div className={classNames('results-modal-counter', { winner: wonPrize && wonTicket })}>
                        <div className="results-modal-counter-title">
                            {translate('your.tickets')}
                            <img className="results-modal-counter-image" src={Ticket}></img>
                        </div>
                        <span className="results-modal-counter-tickets">
                            {userState?.tickets?.map((x) => x.ticket_id).join(', ')}
                        </span>
                    </div>

                    {isMob ? (
                        <img className="results-modal-banner" src={wonTicket ? WinBanner : LoseBanner}></img>
                    ) : null}
                    <div className="results-modal-heading">
                        {translate(wonTicket ? 'results.heading.win' : 'results.heading.lose')}
                    </div>
                    <div
                        className="results-modal-desc"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(
                                translate(
                                    wonPrize
                                        ? wonPrize.modalConfig
                                            ? 'results.desc.win.link'
                                            : 'results.desc.win.admin'
                                        : 'results.desc.lose',
                                    {
                                        ticket: wonTicket?.ticket_id
                                            ? `<span class="results-modal-desc-bold">${wonTicket?.ticket_id}</span>`
                                            : '',
                                        prize: prizeConf
                                            ? `<span class="results-modal-desc-bold">${translate(
                                                  prizeConf.itemNameKey
                                              )}</span>`
                                            : ''
                                    }
                                )
                            )
                        }}
                    ></div>
                </div>
            </Modal>
            {wonPrize?.modalConfig ? (
                <PrizeModal
                    isOpened={prizeModalOpened}
                    background={wonPrize.modalConfig.headerBackground}
                    imgSrc={wonPrize.modalConfig.headerImage}
                    title={wonPrize.modalConfig.title}
                    description={
                        <Label
                            of={wonPrize.modalConfig.description}
                            isRawHtml
                            params={settings.winnerTicketConf?.insertParams}
                        />
                    }
                    primaryButtonText={wonPrize.modalConfig.actionText}
                    onClickPrimaryBtn={() =>
                        openLink(settings.winnerTicketConf?.activateLink || wonPrize.modalConfig!.actionLink, true)
                    }
                    secondaryButtonText={wonPrize.modalConfig.detailsText}
                    onClickSecondaryBtn={() => openLink(wonPrize.modalConfig?.detailsLink || '', true)}
                    onClose={() => setPrizeModalOpened(false)}
                ></PrizeModal>
            ) : null}
            <WinnersModal
                isOpened={winnersModalOpened}
                myTicket={wonTicket}
                onClose={() => setWinnersModalOpened(false)}
            />
        </>
    );
});
